import type { Dispatch, SetStateAction } from 'react';
import { useCallback } from 'react';
import { sleep } from '@frameio/utils';
import { useMoveAssetsWithWarning } from '../../contexts';
import { useVersionAsset } from '../../hooks';
import type { DndOpsContextValue } from '../contexts/DndOpsContext';
import {
  getSourceAndTarget,
  isValidMoveOp,
  isValidVersionStackOp,
} from '../utils/op-checks';
import type { DndOperation } from '../utils/types';

export const useHandleDndOperation = (
  setState: Dispatch<SetStateAction<DndOpsContextValue>>,
  onOperationComplete: () => void
) => {
  const versionAsset = useVersionAsset({
    onError: () => {
      setState((prev) => ({ ...prev, versionStackLifecycle: null }));
      onOperationComplete();
    },
    onSuccess: async () => {
      /** reset the dnd op, but not the version stack phase */
      setState((prev) => ({
        ...prev,
        active: null,
        over: null,
        draggingIds: new Set(),
        versionStackLifecycle: {
          phase: 'completed',
          fromId: prev.active!.id,
          toId: prev.over!.id,
        },
      }));
      await sleep(VERSION_STACK_RESET_DELAY);
      onOperationComplete();
    },
  });

  const moveAssets = useMoveAssetsWithWarning();

  return useCallback(
    async (op: DndOperation) => {
      if (isValidVersionStackOp(op)) {
        setState((prev) => ({
          ...prev,
          versionStackLifecycle: {
            phase: 'dropped',
            fromId: prev.active!.id,
            toId: prev.over!.id,
          },
        }));
        await sleep(VERSION_STACK_INITIAL_DELAY);
        versionAsset(op.activeInfo!.id, op.overInfo!.id);
        return;
      }
      if (isValidMoveOp(op)) {
        const { source, target } = getSourceAndTarget(op);
        if (source?.parent?.id && target) {
          moveAssets({
            assetIds: Array.from(op.draggingIds),
            destination: target,
            sourceId: source.parent.id,
          });
        }
        onOperationComplete();
        return;
      }
      onOperationComplete();
    },
    [moveAssets, onOperationComplete, setState, versionAsset]
  );
};
/** orchestration: ms to wait before performing the version stack mutation, allowing the drop animation time to fully complete */
const VERSION_STACK_INITIAL_DELAY = 100;

/** orchestration: ms to wait after a version stack succeeds to fully reset the lifecycle phase. this allows time for the shuffle animation to calculate */
const VERSION_STACK_RESET_DELAY = 500;
